import * as React from "react";
import classNames from "classnames";

import GenSpan from "../../Gen/Span/component";

interface ReasoningToolDiagnosisSummaryProps {
  uid: string;
  content: string;
  isEditing: boolean;
  startNodeEdit: () => void;
  endNodeEdit: React.FocusEventHandler<HTMLInputElement>;
  isLeadingDiagnosis: boolean;
  isDangerousDiagnosis: boolean;
  setIsDangerousDiagnosis: (uid: string, value: boolean) => void;
  setIsLeadingDiagnosis: (uid: string) => void;
  deleteNode?: (uid: string) => void;
  rowIdx?: number;
  totalRows?: number;
  diagnosisRowRefs?: any;
}


export default function ReasoningToolDiagnosisSummary({
  uid,
  content = "",
  isEditing,
  startNodeEdit,
  endNodeEdit,
  isLeadingDiagnosis = false,
  isDangerousDiagnosis = false,
  setIsDangerousDiagnosis,
  setIsLeadingDiagnosis,
  deleteNode,
  rowIdx,
  totalRows,
  diagnosisRowRefs,
}: ReasoningToolDiagnosisSummaryProps) {
  // Refs for icons
  const dangerousRef = React.useRef(null);
  const leadingRef = React.useRef(null);

  // Keyboard handler for icon buttons
  const handleIconKeyDown = (e, action, iconIdx) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
    // Arrow navigation between icons
    if (e.key === 'ArrowRight' && e.target === dangerousRef.current) {
      e.preventDefault();
      leadingRef.current && leadingRef.current.focus();
    } else if (e.key === 'ArrowLeft' && e.target === leadingRef.current) {
      e.preventDefault();
      dangerousRef.current && dangerousRef.current.focus();
    }
  };

  const handleDeleteKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && deleteNode) {
      e.preventDefault();
      deleteNode(uid);
    }
  };

  // Handler to move focus from content to first icon with Arrow Down/Up
  const handleContentKeyDown = (e) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && dangerousRef.current) {
      e.preventDefault();
      dangerousRef.current.focus();
    }
  };

  return (
    <div className="reasoning-tool-diagnosis-summary">
      <div className="reasoning-tool-diagnosis-summary-content">
        <GenSpan
          value={content}
          placeholder="Enter Diagnosis..."
          shouldAutoFocus={isEditing}
          isEditing={isEditing}
          onSpanClick={startNodeEdit}
          onBlur={endNodeEdit}
          onEnterKeyPress={endNodeEdit}
          onKeyDown={handleContentKeyDown}
        >
          {content}
        </GenSpan>
      </div>
      <div className="reasoning-tool-diagnosis-summary-buttons">
        <i
          ref={dangerousRef}
          role="button"
          tabIndex={0}
          aria-label={isDangerousDiagnosis ? "Mark as not dangerous" : "Mark as dangerous"}
          onClick={() => setIsDangerousDiagnosis(uid, !isDangerousDiagnosis)}
          onKeyDown={e => handleIconKeyDown(e, () => setIsDangerousDiagnosis(uid, !isDangerousDiagnosis), 0)}
          className={classNames("icon-warning", "dangerous", {active: isDangerousDiagnosis})}
        ></i>
        <i
          ref={leadingRef}
          role="button"
          tabIndex={0}
          aria-label={isLeadingDiagnosis ? "Unmark as leading diagnosis" : "Mark as leading diagnosis"}
          onClick={() => setIsLeadingDiagnosis(uid)}
          onKeyDown={e => handleIconKeyDown(e, () => setIsLeadingDiagnosis(uid), 1)}
          className={classNames("icon-star", "leading", {active: isLeadingDiagnosis})}
        ></i>
      </div>
    </div>
  );
}
